import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Mail, MapPin, Users, DollarSign, PhoneCall, Phone } from "lucide-react";
import SiteVisitForm from "@/components/SiteVisitForm";
import VideoBackground from "@/components/VideoBackground";
import { PHONE_HREF, PHONE_DISPLAY, trackPhoneCall } from "@/lib/contact";

const CONTACT_IMG =
  "/media/vending-machine-orange-wall.png";

export default function Contact() {
  return (
    <div data-testid="contact-page" className="text-white">
      <Seo />
      <section className="relative overflow-hidden border-b border-white/5">
        <VideoBackground testId="contact-hero-video" />
        <div className="relative mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center rounded-full border border-[#FE7200]/30 bg-[#FE7200]/10 px-3 py-1 text-xs font-semibold tracking-wide text-[#FE7200]">
              Anytime Vending Machines
            </div>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              Contact Us
            </h1>
            <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-2xl">
              Want a <b className="text-white">free vending machine installed</b> at your workplace (30+ staff or steady foot traffic)?
              Send your details below and we'll get back to you to confirm eligibility and organise a site visit.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href="#book-form"
                data-testid="contact-book-button"
                className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
              >
                Book Free Site Visit
              </a>
              <Link
                to="/"
                data-testid="contact-home-button"
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-white/10 transition-colors"
              >
                Back to Home
              </Link>
            </div>

            <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-300">
              {["Fast response", "No lock-in", "Stocked & serviced"].map((b) => (
                <li key={b} className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-[#FE7200]" /> {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-white/10 bg-[#121212] overflow-hidden">
              <img src={CONTACT_IMG} alt="Vending machine" className="w-full h-44 object-cover" />
              <div className="p-6">
                <h3 className="font-display text-xl font-bold text-white">Quick contact</h3>
                <p className="mt-1 text-sm text-zinc-400">For enquiries, partnerships, or to organise a site visit, use the form below.</p>

                <ul className="mt-5 space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <Phone className="h-4 w-4 text-[#FE7200] mt-0.5" />
                    <div>
                      <div className="text-xs text-zinc-400">Phone</div>
                      <a href={PHONE_HREF} onClick={trackPhoneCall} data-testid="contact-phone-link" className="text-white font-semibold hover:text-[#FE7200] transition-colors">{PHONE_DISPLAY}</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Mail className="h-4 w-4 text-[#FE7200] mt-0.5" />
                    <div>
                      <div className="text-xs text-zinc-400">Email</div>
                      <a href="mailto:info.nector@gmail.com" data-testid="contact-email-link" className="text-white hover:text-[#FE7200]">info.nector@gmail.com</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-[#FE7200] mt-0.5" />
                    <div>
                      <div className="text-xs text-zinc-400">Coverage</div>
                      <div className="text-white">Country-wide</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Users className="h-4 w-4 text-[#FE7200] mt-0.5" />
                    <div>
                      <div className="text-xs text-zinc-400">Best fit</div>
                      <div className="text-white">30–80+ staff or steady foot traffic</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <DollarSign className="h-4 w-4 text-[#FE7200] mt-0.5" />
                    <div>
                      <div className="text-xs text-zinc-400">Cost</div>
                      <div className="text-white">Zero upfront for qualifying sites</div>
                    </div>
                  </li>
                </ul>

                <a
                  href="#book-form"
                  data-testid="contact-go-to-form"
                  className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[#FE7200] px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] text-black hover:bg-[#FF8C2E] transition-colors"
                >
                  Go To Booking Form
                </a>
                <p className="mt-3 text-xs text-zinc-500 text-center">
                  Tip: include staff numbers + address so we can assess quickly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20" data-testid="before-you-submit-section">
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Before you submit</h2>
        <p className="mt-3 text-zinc-300 max-w-3xl">
          The fastest way for us to help is to include your staff numbers and where you'd like the machine placed.
        </p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: "Do we have to buy the machine?", text: <>No — qualifying workplaces can get a machine installed at <b className="text-white">zero upfront cost</b>.</> },
            { title: "What staff numbers qualify?", text: <>Typically <b className="text-white">30–80+</b> is ideal. Foot traffic can also qualify smaller sites.</> },
            { title: "Can we choose products?", text: <>Yes — snacks, drinks, meals, and healthier options. You can update the mix anytime.</> },
          ].map((q, i) => (
            <div key={i} data-testid={`faq-card-${i}`} className="rounded-2xl border border-white/10 bg-[#121212] p-6 hover:border-[#FE7200]/40 transition-colors">
              <h3 className="font-display text-lg font-bold text-white">{q.title}</h3>
              <p className="mt-3 text-sm text-zinc-300 leading-relaxed">{q.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="book-form" className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 py-10 md:py-16" data-testid="contact-form-section">
        <SiteVisitForm source="contact-page" testIdPrefix="contact" />
      </section>
    </div>
  );
}
